import { useTranslation } from 'react-i18next';

/** Eyre-only UI strings. Logto phrases (and custom phrases set by Fleet tools/logto-branding.py) cover the rest. */
const strings = {
  zh: {
    brand: 'Eyre ID',
    headerConsent: '使用 Eyre 账号登录',
    signInTitle: '登录或注册',
    registerTitle: '创建 Eyre 账号',
    continueWith: '通过 {name} 继续',
    consentTitle: '登录{name}',
    consentAllow: 'Eyre ID 将允许{name}访问以下与您有关的信息',
    consentNothing: '{name}只会获得您的登录身份，不会读取其他信息。',
    consentNotice: '只在您信任{name}时继续。授予的权限可以随时撤销。',
    consentRedirect: '继续后将返回 {origin}',
    consentPolicy: '查看{name}的{links}，了解它会如何处理您的数据。',
    privacy: '隐私权政策',
    terms: '服务条款',
    and: '和',
    useAnotherAccount: '使用其他账号',
    cancel: '取消',
    continue: '继续',
    help: '帮助',
    privacyShort: '隐私权',
    termsShort: '条款',
    profile: '名称和个人资料照片',
    email: '邮箱地址',
    phone: '手机号',
    organizations: '组织成员身份',
    roles: '角色',
    identities: '已关联的第三方账号',
    customData: '自定义资料',
    address: '地址',
    unregistered: '这个客户端没有在 Eyre ID 登记，身份由 {host} 声明',
    organization: '授权给组织',
    edit: '编辑',
  },
  en: {
    brand: 'Eyre ID',
    headerConsent: 'Sign in with Eyre ID',
    signInTitle: 'Log in or sign up',
    registerTitle: 'Create your Eyre ID',
    continueWith: 'Continue with {name}',
    consentTitle: 'Sign in to {name}',
    consentAllow: 'Eyre ID will allow {name} to access this info about you',
    consentNothing: '{name} only learns who you are and reads nothing else.',
    consentNotice: 'Only continue if you trust {name}. You can revoke access at any time.',
    consentRedirect: 'You will be returned to {origin}',
    consentPolicy: "Review {name}'s {links} to understand how it handles your data.",
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    and: 'and',
    useAnotherAccount: 'Use another account',
    cancel: 'Cancel',
    continue: 'Continue',
    help: 'Help',
    privacyShort: 'Privacy',
    termsShort: 'Terms',
    profile: 'Name and profile picture',
    email: 'Email address',
    phone: 'Phone number',
    organizations: 'Organization memberships',
    roles: 'Roles',
    identities: 'Linked social accounts',
    customData: 'Custom profile data',
    address: 'Address',
    unregistered: 'This client is not registered with Eyre ID; its identity is claimed by {host}',
    organization: 'Grant to organization',
    edit: 'Edit',
  },
} as const;

export type EyreStringKey = keyof (typeof strings)['zh'];

export const useEyreT = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language.toLowerCase().startsWith('zh');
  const table = isZh ? strings.zh : strings.en;

  const t = (key: EyreStringKey, values: Record<string, string> = {}) =>
    table[key].replaceAll(/{(\w+)}/g, (_, name: string) => values[name] ?? '');

  return { t, isZh, language: i18n.language };
};
